import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  hospitalOnboardingService,
  initialOnboardingData,
  initialFleetOnboardingData
} from '../services/hospitalOnboardingService';
import { useAuth } from './AuthContext';

const HospitalOnboardingContext = createContext(null);

export const HospitalOnboardingProvider = ({ children, initialApplicationId = null, initialPartnerType = null }) => {
  const { user } = useAuth();
  const userId = user?.user_id || user?.id || user?.email || 'guest';

  // Helper to extract applicationId from prop, URL or localStorage
  const getAppIdFromUrlOrStorage = useCallback(() => {
    if (initialApplicationId && !initialApplicationId.startsWith('RISA-APP-')) return initialApplicationId;
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const urlId = params.get('applicationId');
      if (urlId && !urlId.startsWith('RISA-APP-')) return urlId;
      const stored = localStorage.getItem('risa_current_application_id');
      if (stored && !stored.startsWith('RISA-APP-')) return stored;
    }
    return null;
  }, [initialApplicationId]);

  const [applicationId, setApplicationId] = useState(getAppIdFromUrlOrStorage);
  const versionRef = useRef(1);
  const isDirtyRef = useRef(false);
  // True once the server copy of the draft has been loaded for the current
  // application. Until then the in-memory form may only hold the stripped,
  // PII-free browser copy, and saving it would overwrite the full server draft.
  const serverDraftLoadedRef = useRef(false);

  const [formData, setFormData] = useState(() => {
    if (initialPartnerType && typeof localStorage !== 'undefined') {
      localStorage.setItem('risa_partner_type', initialPartnerType);
    }
    const draft = hospitalOnboardingService.getDraft(userId, initialApplicationId);
    if (initialPartnerType && draft.partnerType !== initialPartnerType) {
      return {
        ...(initialPartnerType === 'fleet' ? initialFleetOnboardingData : initialOnboardingData),
        ...draft,
        partnerType: initialPartnerType
      };
    }
    return draft;
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState(null); // { message, type: 'success' | 'info' | 'error' }
  const isFirstRender = useRef(true);

  // Dynamic ambulance types loaded from backend
  const [ambulanceTypes, setAmbulanceTypes] = useState([]);
  const [loadingAmbulanceTypes, setLoadingAmbulanceTypes] = useState(true);

  useEffect(() => {
    let isMounted = true;
    hospitalOnboardingService
      .getAmbulanceTypesLookup()
      .then((data) => {
        if (isMounted && Array.isArray(data) && data.length > 0) {
          setAmbulanceTypes(data);
        }
      })
      .catch((err) => {
        console.warn('Failed to fetch ambulance types lookup in context:', err);
      })
      .finally(() => {
        if (isMounted) setLoadingAmbulanceTypes(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const ambulanceTypeNames = useMemo(() => {
    if (ambulanceTypes && ambulanceTypes.length > 0) {
      return ambulanceTypes.map((t) => (typeof t === 'string' ? t : t.name)).filter(Boolean);
    }
    return [];
  }, [ambulanceTypes]);

  // Sync draft when authenticated user changes
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const currentAppId = getAppIdFromUrlOrStorage();
    const userDraft = hospitalOnboardingService.getDraft(userId, currentAppId);
    setFormData(userDraft);
    if (userDraft.current_step && userDraft.current_step >= 1 && userDraft.current_step <= 6) {
      setCurrentStep(userDraft.current_step);
    }
  }, [userId, getAppIdFromUrlOrStorage]);

  // Sync with backend when applicationId is detected
  useEffect(() => {
    const currentAppId = getAppIdFromUrlOrStorage();
    if (currentAppId && currentAppId !== applicationId) {
      setApplicationId(currentAppId);
    }
  }, [getAppIdFromUrlOrStorage, applicationId]);

  useEffect(() => {
    if (!applicationId) return;

    serverDraftLoadedRef.current = false;
    hospitalOnboardingService.fetchDraft(applicationId).then((draft) => {
      if (draft) {
        serverDraftLoadedRef.current = true;
      }
      if (draft && draft.data) {
        setFormData((prev) => ({
          ...prev,
          ...draft.data,
          application_id: draft.application_id,
          application_code: draft.application_code,
          status: draft.status,
          review_notes: draft.review_notes,
          current_step: draft.current_step || 1,
          version: draft.version
        }));
        versionRef.current = draft.version || 1;
        if (draft.current_step && draft.current_step >= 1 && draft.current_step <= 6) {
          setCurrentStep(draft.current_step);
        }
      }
    }).catch(() => {});
  }, [applicationId]);

  // Pure, debounced auto-save effect (local + backend)
  useEffect(() => {
    // Only autosave when user has actually edited form fields
    if (!isDirtyRef.current) {
      return;
    }

    // If application has been approved, lock re-saving
    if (formData.status === 'APPROVED') {
      return;
    }

    const timer = setTimeout(() => {
      const targetAppId = applicationId || formData.application_id || getAppIdFromUrlOrStorage();

      // 1. Local fallback save (scoped by userId and targetAppId)
      hospitalOnboardingService.saveDraft(formData, userId, targetAppId);

      // 2. Backend autosave if applicationId exists. Skipped until the server
      // draft has been loaded (the dirty flag stays set, so the save runs on the
      // next form update once the load has merged the full server data in).
      if (targetAppId && serverDraftLoadedRef.current) {
        hospitalOnboardingService
          .saveDraftToBackend(targetAppId, formData, currentStep, versionRef.current)
          .then((updated) => {
            if (updated && updated.version) {
              versionRef.current = updated.version;
              isDirtyRef.current = false;
            }
          })
          .catch(async (error) => {
            console.warn('Autosave warning in context:', error?.response?.status || error?.message);

            if (error?.response?.status === 409) {
              try {
                const responseBody = error?.response?.data;
                // Determine if this is a version conflict (optimistic lock) or a
                // permanent status conflict (SUBMITTED / APPROVED / REJECTED).
                // Only version conflicts should be retried; permanent ones must
                // stop the loop immediately to avoid hammering the server.
                const isVersionConflict =
                  responseBody?.code === 'VERSION_CONFLICT' ||
                  responseBody?.message?.toLowerCase().includes('version') ||
                  responseBody?.message?.toLowerCase().includes('conflict');

                const freshDraft = await hospitalOnboardingService.fetchDraft(targetAppId);
                if (freshDraft) {
                  versionRef.current = freshDraft.version || 1;
                  // Merge server status/version fields only — keep user's edits.
                  setFormData((prev) => ({
                    ...prev,
                    version: freshDraft.version,
                    status: freshDraft.status
                  }));

                  if (isVersionConflict) {
                    // Leave isDirtyRef=true → one more retry with correct version.
                  } else {
                    // Permanent conflict (SUBMITTED, APPROVED, REJECTED, etc.)
                    // — stop retrying so we don't loop endlessly.
                    isDirtyRef.current = false;
                  }
                }
              } catch (syncErr) {
                console.error('Failed to sync conflicting draft:', syncErr);
                // On sync failure, stop retrying to avoid an infinite error loop.
                isDirtyRef.current = false;
              }
            }
          });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData, userId, currentStep, applicationId, getAppIdFromUrlOrStorage]);

  // Auto-sync step when form data updates or mounts
  useEffect(() => {
    if (formData.current_step && formData.current_step >= 1 && formData.current_step <= 6) {
      setCurrentStep(formData.current_step);
    }
  }, [formData.current_step]);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 4000);
  }, []);

  /**
   * Update top-level field or bulk fields purely (no storage writes inside updater)
   */
  const updateFields = useCallback((updates) => {
    isDirtyRef.current = true;
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  /**
   * Move to specific step (supports up to 6 steps for fleet onboarding)
   */
  const goToStep = useCallback((stepNumber) => {
    const isFleet = formData.partnerType === 'fleet' || localStorage.getItem('risa_partner_type') === 'fleet';
    const maxSteps = isFleet ? 6 : 5;
    if (stepNumber < 1 || stepNumber > maxSteps) return;
    setCurrentStep(stepNumber);
    setFormData((prev) => ({ ...prev, current_step: stepNumber }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [formData.partnerType]);

  /**
   * Save and advance to next step with step validation
   */
  const saveAndContinue = useCallback(() => {
    const isFleet = formData.partnerType === 'fleet' || localStorage.getItem('risa_partner_type') === 'fleet';
    const maxSteps = isFleet ? 6 : 5;

    // Validate current step before advancing
    const validation = hospitalOnboardingService.validateStep(
      currentStep,
      formData,
      isFleet ? 'fleet' : 'hospital'
    );

    if (!validation.isValid) {
      const firstError = Object.values(validation.errors)[0];
      showToast(firstError || 'Please complete required fields before continuing.', 'error');
      return false;
    }

    if (currentStep < maxSteps) {
      const nextStep = currentStep + 1;
      goToStep(nextStep);
      return true;
    }
    return true;
  }, [currentStep, goToStep, formData, showToast]);

  /**
   * Return to previous step
   */
  const goBack = useCallback(() => {
    if (currentStep > 1) {
      goToStep(currentStep - 1);
    }
  }, [currentStep, goToStep]);

  /**
   * Save and Exit action
   */
  const saveAndExit = useCallback(() => {
    setIsSaving(true);
    const targetAppId = applicationId || formData.application_id || getAppIdFromUrlOrStorage();
    const result = hospitalOnboardingService.saveDraft(formData, userId, targetAppId);
    setTimeout(() => {
      setIsSaving(false);
      if (result.success) {
        showToast('Your progress has been safely saved. You can resume anytime.', 'success');
      }
    }, 300);
  }, [formData, userId, applicationId, getAppIdFromUrlOrStorage, showToast]);

  /**
   * Reset / Start over (preserves fleet fields when in fleet mode)
   */
  const startOver = useCallback((partnerTypeOverride) => {
    const isFleet = partnerTypeOverride === 'fleet' || (!partnerTypeOverride && (formData.partnerType === 'fleet' || localStorage.getItem('risa_partner_type') === 'fleet'));
    const targetAppId = applicationId || formData.application_id || getAppIdFromUrlOrStorage();
    hospitalOnboardingService.clearDraft(userId, targetAppId);
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('risa_current_application_id');
    }
    setApplicationId(null);
    versionRef.current = 1;
    setFormData(isFleet ? JSON.parse(JSON.stringify(initialFleetOnboardingData)) : JSON.parse(JSON.stringify(initialOnboardingData)));
    setCurrentStep(1);
    showToast('Onboarding form has been reset.', 'info');
  }, [userId, applicationId, getAppIdFromUrlOrStorage, formData.partnerType, showToast]);

  /**
   * Submit application (calls backend and updates state)
   */
  const submitApplication = useCallback(async () => {
    setIsSaving(true);
    const targetAppId = applicationId || formData.application_id || getAppIdFromUrlOrStorage();

    if (!targetAppId) {
      setIsSaving(false);
      showToast('No active onboarding application found. Please complete partner registration first.', 'error');
      return null;
    }

    try {
      // Flush latest draft to backend before final submission so DB is up to date
      await hospitalOnboardingService.saveDraftToBackend(targetAppId, formData, currentStep, versionRef.current);
      const backendRes = await hospitalOnboardingService.submitToBackend(targetAppId, true);
      const result = backendRes.data;
      setIsSaving(false);
      if (result) {
        const finalAppCode = result.application_code || result.application_id || targetAppId;
        const isEditUpdate = formData.status === 'SUBMITTED' || formData.status === 'CHANGES_REQUESTED' || Boolean(formData.submitted_at);
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('risa_last_submitted_application_code', finalAppCode);
          localStorage.removeItem('risa_current_application_id');
        }
        hospitalOnboardingService.clearDraft(userId);
        setApplicationId(null);
        versionRef.current = 1;

        setFormData((prev) => ({
          ...prev,
          ...result,
          application_id: targetAppId,
          application_code: finalAppCode,
          status: 'SUBMITTED'
        }));
        showToast(
          isEditUpdate ? 'Application updates saved successfully!' : 'Application submitted successfully!',
          'success'
        );
        return { ...result, isEdit: isEditUpdate };
      }
    } catch (err) {
      setIsSaving(false);
      showToast(err.message || 'Failed to submit application. Please check all fields.', 'error');
      return null;
    }
  }, [formData, userId, applicationId, getAppIdFromUrlOrStorage, showToast]);

  return (
    <HospitalOnboardingContext.Provider
      value={{
        formData,
        setFormData,
        currentStep,
        setCurrentStep: goToStep,
        updateFields,
        saveAndContinue,
        goBack,
        saveAndExit,
        startOver,
        submitApplication,
        isSaving,
        toast,
        showToast,
        applicationId,
        setApplicationId,
        ambulanceTypes,
        ambulanceTypeNames,
        loadingAmbulanceTypes
      }}
    >
      {children}
      {/* Global Onboarding Toast Notification */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 animate-bounce-in max-w-sm">
          <div
            className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-xl text-white text-xs sm:text-sm font-medium ${
              toast.type === 'error'
                ? 'bg-red-600'
                : toast.type === 'info'
                ? 'bg-blue-600'
                : 'bg-emerald-600'
            }`}
          >
            <span>{toast.message}</span>
          </div>
        </div>
      )}
    </HospitalOnboardingContext.Provider>
  );
};

export const useHospitalOnboarding = () => {
  const context = useContext(HospitalOnboardingContext);
  if (!context) {
    throw new Error('useHospitalOnboarding must be used within a HospitalOnboardingProvider');
  }
  return context;
};

export default HospitalOnboardingContext;
